import { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { NAV_ITEMS } from "@/components/feature/navigation";
import { settingsApi } from "@/services/api";
import type { AppSettings } from "@/models/types";

const DEFAULT_SETTINGS: AppSettings = {
  id: "settings",
  currency: "USD",
  language: "es",
  theme: "light",
  userName: "Usuario",
  monthlyBudget: 0,
  notifications: { weeklySummary: true, budgetAlert: true },
};

interface SidebarProps {
  settings?: AppSettings;
}

export default function Sidebar({ settings: propSettings }: SidebarProps) {
  const [localSettings, setLocalSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(!propSettings);

  useEffect(() => {
    // Si no recibimos settings por prop, cargamos desde API
    if (propSettings) {
      setLocalSettings(propSettings);
      setLoading(false);
      return;
    }

    let mounted = true;
    const loadSettings = async () => {
      try {
        const data = await settingsApi.get();
        if (mounted) {
          setLocalSettings({ ...DEFAULT_SETTINGS, ...data });
        }
      } catch (error) {
        console.error("Error cargando configuración en sidebar:", error);
        // Mantener valores por defecto
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadSettings();
    return () => {
      mounted = false;
    };
  }, [propSettings]);

  // Sincronizar si cambia la prop
  useEffect(() => {
    if (propSettings) {
      setLocalSettings(propSettings);
    }
  }, [propSettings]);

  // Mientras se cargan los settings, mostrar un placeholder o los defaults
  const settings = localSettings;

  return (
    <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 bg-background-100 border-r border-background-200">
      {/* Logo */}
      <div className="flex items-center gap-3 px-6 h-16 border-b border-background-200">
        <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
          <i className="ri-wallet-3-fill text-white text-lg" />
        </div>
        <div>
          <p className="font-heading font-bold text-foreground-950 leading-tight">
            Gasto<span className="text-primary-500">Cero</span>
          </p>
          <p className="text-[11px] text-foreground-500">Control de gastos</p>
        </div>
      </div>

      {/* Nuevo gasto */}
      <div className="px-4 pt-4">
        <Link
          to="/gastos/nuevo"
          className="flex items-center justify-center gap-2 w-full rounded-md bg-primary-500 text-white py-2.5 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-add-line text-lg" />
          <span className="whitespace-nowrap">Nuevo gasto</span>
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === "/"}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors cursor-pointer ${
                isActive
                  ? "bg-primary-100 text-primary-700"
                  : "text-foreground-700 hover:bg-background-200"
              }`
            }
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className={`${item.icon} text-lg`} />
            </span>
            <span className="whitespace-nowrap">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div className="px-4 py-4 border-t border-background-200">
        <Link
          to="/configuracion"
          className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-background-200 cursor-pointer transition-colors"
        >
          <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-semibold">
            {settings.userName.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground-900 truncate">
              {settings.userName}
            </p>
            <p className="text-xs text-foreground-500">Ver perfil</p>
          </div>
        </Link>
      </div>
    </aside>
  );
}